/**
 * Appels audio / vidéo des projets (LiveKit).
 * - Écoute les appels entrants sur le socket global (call:incoming…) et affiche la sonnerie.
 * - startCall(projet, kind) lance un appel depuis l'en-tête de conversation.
 * - L'écran d'appel reste monté pendant toute la communication (réductible en vignette).
 */
import React, { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSocketOptional } from './SocketContext';
import { AppContext } from './AppContext';
import { showOsNotification } from '../utils/osNotifications';
import IncomingCallModal from '../components/calls/IncomingCallModal';

// LiveKit (~400 Ko) n'est chargé qu'au premier appel
const CallScreen = lazy(() => import('../components/calls/CallScreen'));

const CallContext = createContext(null);

const emitWithAck = (socket, event, data, timeoutMs = 10000) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, message: 'Le serveur ne répond pas' }), timeoutMs);
    socket.emit(event, data, (res) => {
      clearTimeout(timer);
      resolve(res || { ok: false });
    });
  });

export function CallProvider({ children }) {
  const { socket } = useSocketOptional();
  const { userData } = useContext(AppContext) || {};
  const [incoming, setIncoming] = useState(null); // appel qui sonne
  const [current, setCurrent] = useState(null); // { call, url, token, kind, outgoing }
  const [busy, setBusy] = useState(false);
  const currentRef = useRef(null);
  currentRef.current = current;
  const incomingRef = useRef(null);
  incomingRef.current = incoming;
  const userIdRef = useRef(null);
  userIdRef.current = userData?.id ?? null;

  useEffect(() => {
    if (!socket) return undefined;

    const onIncoming = (call) => {
      if (!call) return;
      // Déjà en communication : on ne fait pas sonner un second appel
      if (currentRef.current) return;
      setIncoming(call);
      if (document.hidden) {
        showOsNotification({
          title: call.kind === 'video' ? 'Appel vidéo entrant' : 'Appel entrant',
          body: `${call.caller?.nom_complet || 'Un membre'} — ${call.projetNom || 'Projet'}`,
          tag: `call-${call.callId}`
        });
      }
    };
    const clearRinging = ({ callId } = {}) =>
      setIncoming((prev) => (prev && (!callId || prev.callId === callId) ? null : prev));
    const onEnded = (data = {}) => {
      const who = data.by?.nom_complet || 'Votre correspondant';
      const byOther = data.by && Number(data.by.id) !== Number(userIdRef.current);
      const ringing = incomingRef.current;
      if (ringing && (!data.callId || ringing.callId === data.callId) && data.reason === 'cancelled' && byOther) {
        toast(`${who} a annulé l’appel`, { icon: '📵' });
      }
      clearRinging(data);
      const cur = currentRef.current;
      if (cur && Number(cur.call.projetId) === Number(data.projetId)) {
        if (data.reason === 'missed' && cur.outgoing) toast('Pas de réponse');
        else if (data.reason === 'hangup' && byOther) toast(`${who} a raccroché`, { icon: '📵' });
        setCurrent(null);
      }
    };
    const onLeft = ({ user, callId } = {}) => {
      const cur = currentRef.current;
      if (cur && cur.call.callId === callId && Number(user?.id) !== Number(userIdRef.current)) {
        toast(`${user?.nom_complet || 'Un membre'} a quitté l’appel`);
      }
    };
    const onDeclined = ({ user, projetId } = {}) => {
      const cur = currentRef.current;
      if (cur && Number(cur.call.projetId) === Number(projetId)) {
        toast(`${user?.nom_complet || 'Un membre'} a refusé l’appel`);
      }
    };
    const onUpdate = (call) => {
      setCurrent((prev) => (prev && prev.call.callId === call?.callId ? { ...prev, call } : prev));
    };

    socket.on('call:incoming', onIncoming);
    socket.on('call:ended', onEnded);
    socket.on('call:answered-elsewhere', clearRinging);
    socket.on('call:declined', onDeclined);
    socket.on('call:update', onUpdate);
    socket.on('call:left', onLeft);
    return () => {
      socket.off('call:left', onLeft);
      socket.off('call:incoming', onIncoming);
      socket.off('call:ended', onEnded);
      socket.off('call:answered-elsewhere', clearRinging);
      socket.off('call:declined', onDeclined);
      socket.off('call:update', onUpdate);
    };
  }, [socket]);

  // Fermeture de l'onglet pendant un appel : prévenir le serveur
  useEffect(() => {
    if (!current || !socket) return undefined;
    const onUnload = () => socket.emit('call:leave', { projetId: current.call.projetId });
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [current, socket]);

  const startCall = useCallback(
    async (projetId, kind = 'audio') => {
      if (!socket?.connected) {
        toast.error('Connexion au serveur indisponible, réessayez dans un instant');
        return;
      }
      if (currentRef.current) {
        toast('Un appel est déjà en cours');
        return;
      }
      setBusy(true);
      const res = await emitWithAck(socket, 'call:start', { projetId, kind });
      setBusy(false);
      if (!res.ok) {
        toast.error(res.message || 'Impossible de lancer l’appel');
        return;
      }
      setCurrent({ call: res.call, url: res.url, token: res.token, kind: res.call.kind, outgoing: !res.joined });
    },
    [socket]
  );

  const accept = useCallback(async () => {
    const call = incoming;
    if (!call || !socket) return;
    setIncoming(null);
    const res = await emitWithAck(socket, 'call:join', { projetId: call.projetId });
    if (!res.ok) {
      toast.error(res.message || 'Impossible de rejoindre l’appel');
      return;
    }
    setCurrent({ call: res.call, url: res.url, token: res.token, kind: res.call.kind, outgoing: false });
  }, [incoming, socket]);

  const decline = useCallback(() => {
    if (!incoming || !socket) return;
    socket.emit('call:decline', { projetId: incoming.projetId });
    setIncoming(null);
  }, [incoming, socket]);

  const hangup = useCallback(() => {
    const cur = currentRef.current;
    if (!cur) return;
    socket?.emit('call:leave', { projetId: cur.call.projetId });
    setCurrent(null);
  }, [socket]);

  const value = useMemo(
    () => ({ startCall, inCall: !!current, busy, currentProjetId: current?.call.projetId ?? null }),
    [startCall, current, busy]
  );

  return (
    <CallContext.Provider value={value}>
      {children}
      {incoming ? <IncomingCallModal call={incoming} onAccept={accept} onDecline={decline} /> : null}
      {current ? (
        <Suspense fallback={null}>
        <CallScreen
          key={current.call.callId}
          url={current.url}
          token={current.token}
          call={current.call}
          kind={current.kind}
          outgoing={current.outgoing}
          meId={userData?.id}
          onHangup={hangup}
        />
        </Suspense>
      ) : null}
    </CallContext.Provider>
  );
}

export function useCall() {
  return useContext(CallContext) || { startCall: () => {}, inCall: false, busy: false, currentProjetId: null };
}
